import { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlarmClock, CheckCircle2, ClipboardList, Gauge, MessageSquareHeart, ShieldAlert } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { KpiStrip } from '../components/quality/KpiStrip';
import { DueSoonBanner } from '../components/quality/DueSoonBanner';
import { TaskCard } from '../components/quality/TaskCard';
import { isOpen } from '../components/quality/qualityConfig';

const TABS = [
    { id: 'active', label: 'Active', filter: isOpen, empty: 'No open tasks right now. New tasks from your Quality reviewer will show up here.' },
    { id: 'completed', label: 'Completed', filter: (t) => t.status === 'completed', empty: 'Completed tasks and your feedback will appear here.' },
    { id: 'missed', label: 'Missed', filter: (t) => t.status === 'missed', empty: 'Nice — you have not missed any deadlines.' },
];

const kpis = (s) => [
    { id: 'open', label: 'Open tasks', value: s.open, icon: ClipboardList, tone: 'text-violet-500' },
    { id: 'due-soon', label: 'Due in 48h', value: s.due_soon, icon: AlarmClock, tone: 'text-amber-500' },
    { id: 'completed', label: 'Completed', value: s.completed, icon: CheckCircle2, tone: 'text-emerald-500' },
    { id: 'avg-score', label: 'Avg task score', value: s.avg_score, icon: Gauge, tone: 'text-indigo-500', hint: 'Completed + missed tasks' },
    { id: 'feedback', label: 'Awaiting feedback', value: s.awaiting_feedback, icon: MessageSquareHeart, tone: 'text-fuchsia-500' },
];

const Denied = () => (
    <div className="flex flex-col items-center gap-3 rounded-2xl border p-12 text-center" data-testid="quality-tasks-denied">
        <ShieldAlert className="h-10 w-10 text-muted-foreground" />
        <p className="text-muted-foreground">Quality Tasks are available to users with Staffing Training access.</p>
    </div>
);

export default function QualityTasks() {
    const { api } = useAuth();
    const [state, setState] = useState({ loading: true, tasks: [], summary: null, denied: false });
    const [tab, setTab] = useState('active');

    const load = useCallback(() => api.get('/quality-tasks/my')
        .then(({ data }) => setState({ loading: false, denied: false, ...data }))
        .catch((err) => setState((s) => ({ ...s, loading: false, denied: err.response?.status === 403 }))), [api]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        api.post('/quality-tasks/my/seen')
            .then(() => window.dispatchEvent(new Event('quality-tasks-updated')))
            .catch(() => {});
    }, [api]);

    const current = TABS.find((t) => t.id === tab);
    const visible = useMemo(() => state.tasks.filter(current.filter), [state.tasks, current]);

    return (
        <MainLayout>
            <div className="space-y-6" data-testid="quality-tasks-page">
                <header>
                    <p className="font-mono text-xs uppercase tracking-[0.2em] text-violet-600 dark:text-violet-300">Quality</p>
                    <h1 className="mt-1 font-heading text-4xl font-bold tracking-tight sm:text-5xl">Quality Tasks</h1>
                    <p className="mt-2 max-w-2xl text-muted-foreground">Simulations your Quality reviewer picked after listening to your calls. Your best score before the deadline counts.</p>
                </header>
                {state.loading ? <Skeleton className="h-28 w-full rounded-xl" /> : state.denied ? <Denied /> : (
                    <div className="space-y-6">
                        <DueSoonBanner summary={state.summary} />
                        <KpiStrip items={kpis(state.summary)} testId="quality-tasks-kpis" />
                        <Tabs value={tab} onValueChange={setTab}>
                            <TabsList data-testid="quality-tasks-tabs">
                                {TABS.map((t) => (
                                    <TabsTrigger key={t.id} value={t.id} data-testid={`quality-tasks-tab-${t.id}`}>
                                        {t.label} <span className="ml-1.5 font-mono text-xs opacity-70">{state.tasks.filter(t.filter).length}</span>
                                    </TabsTrigger>
                                ))}
                            </TabsList>
                        </Tabs>
                        <AnimatePresence mode="wait">
                            <motion.div key={tab} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.18 }}>
                                {visible.length ? (
                                    <div className="grid gap-5 xl:grid-cols-2">
                                        {visible.map((task, i) => <TaskCard key={task.id} task={task} index={i} onChanged={load} />)}
                                    </div>
                                ) : (
                                    <div className="rounded-2xl border border-dashed p-12 text-center text-muted-foreground" data-testid={`quality-tasks-empty-${tab}`}>{current.empty}</div>
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </div>
                )}
            </div>
        </MainLayout>
    );
}
